import React, { useState } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { EditRubricSectionThread } from 'apps/sigma2-ui/src/components/rubrics/EditRubricSectionThread';
import { FormControl } from '@mui/material';
import { Slider } from '@mui/material';
import { OutlinedInput } from '@mui/material';
import { FormLabel } from '@mui/material';
import { FormGroup } from '@mui/material';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TertiaryButton } from 'apps/sigma2-ui/src/components/TertiaryButton';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { IconTrashCanSvg } from 'apps/sigma2-ui/src/components/svg/IconTrashCan';
import { S2NumberField } from 'apps/sigma2-ui/src/filters-v2/components/S2NumberField';

export interface EditRubricEditCategorySectionCriteriaThreadProps {
  weight: number;
  text: string;
  onChangeWeight: (weight: number) => void;
  onChangeText: (text: string) => void;
  onRemove: () => void;
  requiredCriteriaError: boolean;
}

export const EditRubricEditCategorySectionCriteriaThread: React.FC<
  EditRubricEditCategorySectionCriteriaThreadProps
> = ({
  weight,
  onChangeWeight,
  text,
  onChangeText,
  onRemove,
  requiredCriteriaError,
}) => {
  const language = useLanguage();
  const [touched, setTouched] = useState(false);

  return (
    <EditRubricSectionThread>
      <FormGroup className="w-full">
        <Spacing direction="vertical" size="md" className="py-2">
          <FormControl>
            <div className="flex justify-between align-middle pb-4">
              <FormLabel id="edit-rubric-category-section-criteria-label">
                <S2Typography
                  variant="body-6-bold"
                  textColorVariant="gray-1000"
                  translatedContent="Criteria"
                />
              </FormLabel>
              <TertiaryButton
                typographyClassName="!text-indigo-1000"
                className="!text-indigo-1000"
                label={language.remove()}
                icon={<IconTrashCanSvg />}
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    onRemove();
                  },
                }}
              />
            </div>
            <OutlinedInput
              aria-labelledby="edit-rubric-category-section-criteria-label"
              placeholder="Candidate should display exemplary organizational skills..."
              value={text}
              onChange={(e) => {
                if (!touched) setTouched(true);
                onChangeText(e.currentTarget.value);
              }}
              multiline
              rows={2}
              error={touched && requiredCriteriaError}
            />
          </FormControl>
          <Spacing direction="vertical" size="sm">
            <S2Typography
              variant="body-6-bold"
              textColorVariant="gray-700"
              translatedContent="Weight"
            />
            <Spacing direction="horizontal" size="lg" className="items-center">
              <S2Typography
                variant="body-6-bold"
                textColorVariant="gray-700"
                translatedContent="1"
                dangerouslyOverrideClassNameFn={(cn) => `${cn} flex-none`}
              />
              <Slider
                min={1}
                max={10}
                value={weight}
                onChange={(_, v) => onChangeWeight(v)}
                className="flex-1"
                marks
                sx={{
                  '& .MuiSlider-rail': {
                    height: 8,
                    background: `
                    linear-gradient(
                      to right,
                      #3A3AE8 0%,
                      #080857 100%
                    )
                  `,
                    opacity: 1,
                  },
                  '& .MuiSlider-track': {
                    opacity: 0,
                    height: 8,
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: 'red',
                    opacity: 1,
                    height: 16,
                    width: 16,
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                  },
                  '& .MuiSlider-mark[data-index="0"]': {
                    backgroundColor: '#3A3AE8',
                  },
                  '& .MuiSlider-mark[data-index="1"]': {
                    backgroundColor: '#3535D8',
                  },
                  '& .MuiSlider-mark[data-index="2"]': {
                    backgroundColor: '#2F2FC7',
                  },
                  '& .MuiSlider-mark[data-index="3"]': {
                    backgroundColor: '#2A2AB6',
                  },
                  '& .MuiSlider-mark[data-index="4"]': {
                    backgroundColor: '#2525A6',
                  },
                  '& .MuiSlider-mark[data-index="5"]': {
                    backgroundColor: '#1F1F95',
                  },
                  '& .MuiSlider-mark[data-index="6"]': {
                    backgroundColor: '#1A1A84',
                  },
                  '& .MuiSlider-mark[data-index="7"]': {
                    backgroundColor: '#151573',
                  },
                  '& .MuiSlider-mark[data-index="8"]': {
                    backgroundColor: '#0F0F63',
                  },
                  '& .MuiSlider-mark[data-index="9"]': {
                    backgroundColor: '#080857',
                  },
                  '& .MuiSlider-thumb': {
                    height: 16,
                    width: 16,
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    border: '2px solid #FFF',
                    outline: '2px solid #080857',
                  },
                  '& .MuiSlider-thumb::before': {
                    boxShadow: 'none',
                  },
                }}
              />
              <S2Typography
                variant="body-6-bold"
                textColorVariant="gray-700"
                translatedContent="10"
                dangerouslyOverrideClassNameFn={(cn) => `${cn} flex-none`}
              />
              <S2NumberField
                min={1}
                max={10}
                className="flex-none"
                width="77px"
                value={weight}
                hideArrowButtons
                onValueCommitted={(v) => {
                  if (!v) {
                    return;
                  }

                  onChangeWeight(v);
                }}
              />
            </Spacing>
          </Spacing>
        </Spacing>
      </FormGroup>
    </EditRubricSectionThread>
  );
};
